"use client"

import { useState, useRef } from "react"
import { useSelector, shallowEqual } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  Toolbar,
  IconButton,
  OutlinedInput,
  InputAdornment,
  Popover,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Badge,
  ListItemButton,
  ListItemText,
  Tooltip,
} from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useTheme } from "@mui/material/styles"
import MapIcon from "@mui/icons-material/Map"
import DnsIcon from '@mui/icons-material/Dns';
import AddIcon from "@mui/icons-material/Add"
import TuneIcon from "@mui/icons-material/Tune"
import { useTranslation } from "../common/components/LocalizationProvider"
import { useDeviceReadonly } from "../common/util/permissions"
import { isDeviceMoving } from "../common/util/deviceColors"
import DeviceRow from "./DeviceRow"
// 2ECOMP: PremiumUI Refresh — constante para largura do painel
import { DEVICE_LIST_WIDTH } from "../common/theme/layout"

const useStyles = makeStyles()((theme) => ({
  toolbar: {
    display: "flex",
    gap: theme.spacing(1),
  },
  filterPanel: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
    gap: theme.spacing(2),
    // 2ECOMP: PremiumUI Refresh — largura limitada ao painel lateral (280px)
    width: DEVICE_LIST_WIDTH,
    maxHeight: "70vh",
    overflowY: "auto",
  },
}))

const MainToolbar = ({
  filteredDevices,
  devicesOpen,
  setDevicesOpen,
  mapOnlyMode,
  setMapOnlyMode,
  keyword,
  setKeyword,
  filter,
  setFilter,
  filterSort,
  setFilterSort,
  filterMap,
  setFilterMap,
  searchFields,
  setSearchFields,
}) => {
  const { classes } = useStyles()
  const theme = useTheme()
  const navigate = useNavigate()
  const t = useTranslation()

  const deviceReadonly = useDeviceReadonly()

  // 2ECOMP: shallowEqual evita re-renders quando dados NÃO mudaram
  // NOTA: shallowEqual NÃO bloqueia atualizações - apenas compara referências
  const groups = useSelector((state) => state.groups.items, shallowEqual)
  const devices = useSelector((state) => state.devices.items, shallowEqual)
  const positions = useSelector((state) => state.session.positions, shallowEqual)

  const toolbarRef = useRef()
  const inputRef = useRef()
  const [filterAnchorEl, setFilterAnchorEl] = useState(null)
  const [devicesAnchorEl, setDevicesAnchorEl] = useState(null)

  const deviceStatusCount = (status) => Object.values(devices).filter((d) => d.status === status).length

  // Count devices by motion state
  const motionStateCount = (motionState) => {
    return Object.values(devices).filter((device) => {
      const position = positions[device.id]
      if (device.status !== "online") return false
      const moving = isDeviceMoving(position)
      if (motionState === "moving") return moving
      if (motionState === "stopped") return !moving && position
      return false
    }).length
  }

  const hasActiveFilters =
    filter.statuses.length > 0 ||
    filter.groups.length > 0 ||
    (filter.motionStates?.length || 0) > 0 ||
    searchFields.length !== 1 ||
    !searchFields.includes("all")

  const getFieldLabel = (field) => {
    const fieldLabels = {
      all: t("sharedAll") || "Todos",
      name: t("sharedName") || "Nome",
      uniqueId: t("deviceIdentifier") || "IMEI/ID",
      phone: t("sharedPhone") || "Telefone",
      contact: t("sharedContact") || "Contato",
      model: t("deviceModel") || "Modelo",
      category: t("sharedCategory") || "Categoria",
      "attributes.form.operator": "Operadora",
      "attributes.form.vendor": "Vendedor",
      "attributes.form.iccid": "ICCID",
      "attributes.form.fuelConsumption": "Consumo",
      "attributes.form.plate": "Placa",
      "attributes.form.vin": "Renavan",
      "attributes.form.chassi": "Chassi",
      "attributes.form.color": "Cor",
      "attributes.form.brand": "Marca",
      "attributes.form.model": "Modelo Veículo",
    }
    return fieldLabels[field] || field
  }

  return (
    <Toolbar ref={toolbarRef} className={classes.toolbar}>
      <IconButton
        edge="start"
        onClick={() => {
          const next = !devicesOpen
          setDevicesOpen(next)
          setMapOnlyMode(!next)
        }}
      >
        {mapOnlyMode ? <DnsIcon /> : <MapIcon />}
      </IconButton>
      <OutlinedInput
        ref={inputRef}
        placeholder={t("sharedSearchDevices") || "Pesquisar Dispositivos"}
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        onFocus={() => setDevicesAnchorEl(toolbarRef.current)}
        onBlur={() => setDevicesAnchorEl(null)}
        endAdornment={
          <InputAdornment position="end">
            <IconButton size="small" edge="end" onClick={() => setFilterAnchorEl(inputRef.current)}>
              <Badge color="info" variant="dot" invisible={!hasActiveFilters}>
                <TuneIcon fontSize="small" />
              </Badge>
            </IconButton>
          </InputAdornment>
        }
        size="small"
        fullWidth
      />
      <Popover
        open={!!devicesAnchorEl && !devicesOpen}
        anchorEl={devicesAnchorEl}
        onClose={() => setDevicesAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: Number(theme.spacing(2).slice(0, -2)),
        }}
        marginThreshold={0}
        slotProps={{
          paper: {
            style: { width: `calc(${toolbarRef.current?.clientWidth}px - ${theme.spacing(4)})` },
          },
        }}
        elevation={1}
        disableAutoFocus
        disableEnforceFocus
      >
        {filteredDevices.slice(0, 3).map((device) => (
          <DeviceRow key={device.id} device={device} position={positions[device.id]} />
        ))}
        {filteredDevices.length > 3 && (
          <ListItemButton alignItems="center" onClick={() => setDevicesOpen(true)}>
            <ListItemText primary={t("notificationAlways") || "Ver Todos"} style={{ textAlign: "center" }} />
          </ListItemButton>
        )}
      </Popover>
      <Popover
        open={!!filterAnchorEl}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <div className={classes.filterPanel}>
          <FormControl fullWidth size="small">
            <InputLabel>{t("sharedSearchIn") || "Buscar em"}</InputLabel>
            <Select
              label={t("sharedSearchIn") || "Buscar em"}
              value={searchFields}
              onChange={(e) => {
                const value = e.target.value
                if (value.includes("all")) {
                  setSearchFields(["all"])
                } else {
                  setSearchFields(value.filter((v) => v !== "all"))
                }
              }}
              multiple
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 400,
                    overflowY: "auto",
                  },
                },
              }}
              renderValue={(selected) => {
                if (selected.includes("all")) {
                  return t("sharedAll") || "Todos"
                }
                return selected.map((field) => getFieldLabel(field)).join(", ")
              }}
            >
              <MenuItem value="all">
                <Checkbox checked={searchFields.includes("all")} size="small" />
                <ListItemText primary={t("sharedAll") || "Todos"} primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="name">
                <Checkbox checked={searchFields.includes("name")} size="small" />
                <ListItemText primary={t("sharedName") || "Nome"} primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="uniqueId">
                <Checkbox checked={searchFields.includes("uniqueId")} size="small" />
                <ListItemText
                  primary={t("deviceIdentifier") || "IMEI/ID"}
                  primaryTypographyProps={{ fontSize: "0.875rem" }}
                />
              </MenuItem>
              <MenuItem value="phone">
                <Checkbox checked={searchFields.includes("phone")} size="small" />
                <ListItemText
                  primary={t("sharedPhone") || "Telefone"}
                  primaryTypographyProps={{ fontSize: "0.875rem" }}
                />
              </MenuItem>
              <MenuItem value="contact">
                <Checkbox checked={searchFields.includes("contact")} size="small" />
                <ListItemText
                  primary={t("sharedContact") || "Contato"}
                  primaryTypographyProps={{ fontSize: "0.875rem" }}
                />
              </MenuItem>
              <MenuItem value="model">
                <Checkbox checked={searchFields.includes("model")} size="small" />
                <ListItemText
                  primary={t("deviceModel") || "Modelo"}
                  primaryTypographyProps={{ fontSize: "0.875rem" }}
                />
              </MenuItem>
              <MenuItem value="attributes.form.operator">
                <Checkbox checked={searchFields.includes("attributes.form.operator")} size="small" />
                <ListItemText primary="Operadora" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.form.vendor">
                <Checkbox checked={searchFields.includes("attributes.form.vendor")} size="small" />
                <ListItemText primary="Vendedor" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.form.iccid">
                <Checkbox checked={searchFields.includes("attributes.form.iccid")} size="small" />
                <ListItemText primary="ICCID" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.form.fuelConsumption">
                <Checkbox checked={searchFields.includes("attributes.form.fuelConsumption")} size="small" />
                <ListItemText primary="Consumo" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.form.plate">
                <Checkbox checked={searchFields.includes("attributes.form.plate")} size="small" />
                <ListItemText primary="Placa" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.form.vin">
                <Checkbox checked={searchFields.includes("attributes.form.vin")} size="small" />
                <ListItemText primary="Renavan" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.form.chassi">
                <Checkbox checked={searchFields.includes("attributes.form.chassi")} size="small" />
                <ListItemText primary="Chassi" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.form.color">
                <Checkbox checked={searchFields.includes("attributes.form.color")} size="small" />
                <ListItemText primary="Cor" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.form.brand">
                <Checkbox checked={searchFields.includes("attributes.form.brand")} size="small" />
                <ListItemText primary="Marca" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
              <MenuItem value="attributes.form.model">
                <Checkbox checked={searchFields.includes("attributes.form.model")} size="small" />
                <ListItemText primary="Modelo Veículo" primaryTypographyProps={{ fontSize: "0.875rem" }} />
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>{t("deviceStatus") || "Status"}</InputLabel>
            <Select
              label={t("deviceStatus") || "Status"}
              value={filter.statuses}
              onChange={(e) => setFilter({ ...filter, statuses: e.target.value })}
              multiple
            >
              <MenuItem value="online">{`${t("deviceStatusOnline") || "Online"} (${deviceStatusCount("online")})`}</MenuItem>
              <MenuItem value="offline">{`${t("deviceStatusOffline") || "Offline"} (${deviceStatusCount("offline")})`}</MenuItem>
              <MenuItem value="unknown">{`${t("deviceStatusUnknown") || "Desconhecido"} (${deviceStatusCount("unknown")})`}</MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>{t("deviceMotion") || "Movimento"}</InputLabel>
            <Select
              label={t("deviceMotion") || "Movimento"}
              value={filter.motionStates || []}
              onChange={(e) => setFilter({ ...filter, motionStates: e.target.value })}
              multiple
            >
              <MenuItem value="moving">{`${t("deviceMoving") || "Em Movimento"} (${motionStateCount("moving")})`}</MenuItem>
              <MenuItem value="stopped">{`${t("deviceStopped") || "Parado"} (${motionStateCount("stopped")})`}</MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>{t("settingsGroups") || "Grupos"}</InputLabel>
            <Select
              label={t("settingsGroups") || "Grupos"}
              value={filter.groups}
              onChange={(e) => setFilter({ ...filter, groups: e.target.value })}
              multiple
            >
              {Object.values(groups)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>{t("sharedSortBy") || "Ordenar por"}</InputLabel>
            <Select
              label={t("sharedSortBy") || "Ordenar por"}
              value={filterSort}
              onChange={(e) => setFilterSort(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">{"\u00a0"}</MenuItem>
              <MenuItem value="name">{t("sharedName") || "Nome"}</MenuItem>
              <MenuItem value="lastUpdate">{t("deviceLastUpdate") || "Última Atualização"}</MenuItem>
              <MenuItem value="phone">{t("sharedPhone")}</MenuItem>
              <MenuItem value="uniqueId">IMEI</MenuItem>
              <MenuItem value="attributes.form.plate">{t("sharedDevicePlate")}</MenuItem>
              <MenuItem value="attributes.form.operator">{t("sharedOperatorChip")}</MenuItem>
              <MenuItem value="attributes.form.brand">{t("deviceBrand")}</MenuItem>
              <MenuItem value="attributes.form.model">Modelo Veículo</MenuItem>
              <MenuItem value="attributes.form.vendor">Vendedor</MenuItem>
            </Select>
          </FormControl>
          <FormGroup>
            <FormControlLabel
              control={<Checkbox checked={filterMap} onChange={(e) => setFilterMap(e.target.checked)} />}
              label={t("sharedFilterMap") || "Filtrar Mapa"}
            />
          </FormGroup>
        </div>
      </Popover>
      <IconButton edge="end" onClick={() => navigate("/settings/device")} disabled={deviceReadonly}>
        <Tooltip
          open={!deviceReadonly && Object.keys(devices).length === 0}
          title={t("deviceRegisterFirst") || "Registre o primeiro dispositivo"}
          arrow
        >
          <AddIcon />
        </Tooltip>
      </IconButton>
    </Toolbar>
  )
}

export default MainToolbar